import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  useLocation,
  useNavigate,
} from "react-router-dom";

import toast, {
  Toaster,
} from "react-hot-toast";

import Layout from "../layouts/Layout";

import {
  API_BASE_URL,
} from "../layouts/constants";

/* =========================================================
   HELPERS
========================================================= */

function addCacheBuster(url) {
  const separator =
    url.includes("?")
      ? "&"
      : "?";

  return `${url}${separator}_t=${Date.now()}`;
}

async function readJsonSafe(
  response
) {
  const contentType =
    response.headers.get(
      "content-type"
    ) || "";

  if (
    contentType.includes(
      "application/json"
    )
  ) {
    return response.json();
  }

  const text =
    await response.text();

  return {
    success: false,

    message:
      text ||
      `Unexpected server response (${response.status})`,
  };
}

async function fetchNoCache(
  url,
  token,
  options = {}
) {
  const method =
    String(
      options.method || "GET"
    ).toUpperCase();

  const isGet =
    method === "GET";

  const finalUrl =
    isGet
      ? addCacheBuster(url)
      : url;

  const response =
    await fetch(
      finalUrl,
      {
        ...options,

        method,

        cache:
          "no-store",

        credentials:
          "same-origin",

        headers: {
          Accept:
            "application/json",

          "Cache-Control":
            "no-cache, no-store, must-revalidate",

          Pragma:
            "no-cache",

          Expires:
            "0",

          Authorization:
            `Bearer ${token}`,

          ...(options.headers ||
            {}),
        },
      }
    );

  const data =
    await readJsonSafe(
      response
    );

  return {
    response,
    data,
  };
}

function getToken() {
  return (
    localStorage.getItem(
      "token"
    ) ||
    localStorage.getItem(
      "authToken"
    ) ||
    ""
  );
}

function clearAuthAndRedirect(
  navigate
) {
  localStorage.clear();

  navigate("/", {
    replace: true,
  });
}

function normalizeUserStatus(
  user
) {
  const rawApprove =
    String(
      user.fld_Approve ??
      user.status ??
      "pending"
    )
      .trim()
      .toLowerCase();

  const rawActive =
    Number(
      user.is_active ?? 0
    );

  if (
    rawApprove ===
      "approved" &&
    rawActive === 1
  ) {
    return "approved";
  }

  if (
    rawApprove ===
      "disapproved" ||
    rawApprove ===
      "rejected"
  ) {
    return "disapproved";
  }

  if (
    rawApprove ===
      "pending"
  ) {
    return "pending";
  }

  if (
    rawActive === 0
  ) {
    return "disapproved";
  }

  return "pending";
}

function normalizeUser(
  user,
  index,
  fallbackTenantCode
) {
  return {
    id:
      user.SINo ??
      user.SlNo ??
      index + 1,

    member_id:
      user.fld_memberId ??
      user.member_id ??
      "",

    guard_id:
      user.fld_guard_id ??
      "",

    tenant_code:
      user.tenant_code ??
      fallbackTenantCode ??
      "",

    name:
      user.fld_name ??
      user.name ??
      "",

    username:
      user.fld_username ??
      user.username ??
      "",

    email:
      user.fld_mail ??
      user.email ??
      "",

    password_display:
      "********",

    role:
      user.fld_category ??
      user.role ??
      "Guard",

    designation:
      user.fld_designation ??
      user.designation ??
      "",

    year_starts:
      user.fld_year_starts ??
      "",

    year_ends:
      user.fld_year_ends ??
      "",

    dept:
      user.fld_dept ??
      "",

    phone:
      user.fld_phone ??
      "",

    approve_raw:
      user.fld_Approve ??
      "",

    is_active:
      Number(
        user.is_active ?? 0
      ),

    status:
      normalizeUserStatus(
        user
      ),
  };
}

/* =========================================================
   COMPONENT
========================================================= */

export default function SentinelUserList() {
  const navigate =
    useNavigate();

  const location =
    useLocation();

  const mountedRef =
    useRef(true);

  const requestIdRef =
    useRef(0);

  const [
    loading,
    setLoading,
  ] = useState(true);

  const [
    refreshing,
    setRefreshing,
  ] = useState(false);

  const [
    statusUpdatingMemberId,
    setStatusUpdatingMemberId,
  ] = useState("");

  const [
    deleting,
    setDeleting,
  ] = useState(false);

  const [
    dashboardData,
    setDashboardData,
  ] = useState({
    company_name: "",
    tenant_code: "",
    member_prefix: "",
    licensedFor: "",
    admin_active: 0,
    company_address: "",
    company_phone: "",
    admin_email: "",
    company_logo: "",
    user_map: "",
  });

  const [
    users,
    setUsers,
  ] = useState([]);

  const [
    searchTerm,
    setSearchTerm,
  ] = useState("");

  const [
    roleFilter,
    setRoleFilter,
  ] = useState("");

  const [
    statusFilter,
    setStatusFilter,
  ] = useState("");

  const [
    deleteModal,
    setDeleteModal,
  ] = useState({
    open: false,
    memberId: "",
    userName: "",
  });

  const [
    currentPage,
    setCurrentPage,
  ] = useState(1);

  const usersPerPage = 10;

  /* =======================================================
     LOCAL DATA
  ======================================================= */

  function getLocalDashboardFallback() {
    let userData = {};

    try {
      userData =
        JSON.parse(
          localStorage.getItem(
            "userData"
          ) || "{}"
        );
    } catch (error) {
      console.error(
        "[SentinelUserList] Invalid userData:",
        error
      );

      userData = {};
    }

    return {
      company_name:
        localStorage.getItem(
          "company_name"
        ) ||
        userData.company_name ||
        "",

      tenant_code:
        localStorage.getItem(
          "tenant_code"
        ) ||
        userData.tenant_code ||
        "",

      member_prefix:
        localStorage.getItem(
          "member_prefix"
        ) ||
        userData.member_prefix ||
        "",

      licensedFor:
        localStorage.getItem(
          "licensedFor"
        ) ||
        userData.licensedFor ||
        "",

      admin_active:
        Number(
          localStorage.getItem(
            "admin_active"
          ) ??
          userData.admin_active ??
          0
        ),

      company_address:
        localStorage.getItem(
          "company_address"
        ) ||
        userData.company_address ||
        "",

      company_phone:
        localStorage.getItem(
          "company_phone"
        ) ||
        userData.company_phone ||
        "",

      admin_email:
        localStorage.getItem(
          "admin_email"
        ) ||
        userData.admin_email ||
        "",

      company_logo:
        localStorage.getItem(
          "company_logo"
        ) ||
        userData.company_logo ||
        "",

      user_map:
        localStorage.getItem(
          "user_map"
        ) ||
        userData.user_map ||
        "",
    };
  }

  function saveDashboardDataToStorage(
    companyDetails,
    localFallback
  ) {
    /*
      API values are authoritative.

      localStorage is used only when the API did not return
      a particular property.
    */
    const apiTenantCode =
      String(
        companyDetails
          ?.tenant_code ||
        ""
      )
        .trim()
        .toUpperCase();

    const tenantCode =
      apiTenantCode ||
      String(
        localFallback
          ?.tenant_code ||
        ""
      )
        .trim()
        .toUpperCase();

    const apiMemberPrefix =
      Number(
        companyDetails
          ?.member_prefix
      );

    const memberPrefix =
      Number.isInteger(
        apiMemberPrefix
      ) &&
      apiMemberPrefix > 0
        ? apiMemberPrefix
        : localFallback
            ?.member_prefix ||
          "";

    const updatedData = {
      company_name:
        companyDetails
          ?.company_name ??
        localFallback
          ?.company_name ??
        "",

      tenant_code:
        tenantCode,

      member_prefix:
        memberPrefix,

      licensedFor:
        companyDetails
          ?.licensedFor ??
        localFallback
          ?.licensedFor ??
        "",

      admin_active:
        Number(
          companyDetails
            ?.admin_active ??
          localFallback
            ?.admin_active ??
          0
        ),

      company_address:
        companyDetails
          ?.company_address ??
        localFallback
          ?.company_address ??
        "",

      company_phone:
        companyDetails
          ?.company_phone ??
        localFallback
          ?.company_phone ??
        "",

      admin_email:
        companyDetails
          ?.admin_email ??
        localFallback
          ?.admin_email ??
        "",

      company_logo:
        companyDetails
          ?.company_logo ??
        localFallback
          ?.company_logo ??
        "",

      user_map:
        companyDetails
          ?.user_map ??
        localFallback
          ?.user_map ??
        "",
    };

    let currentUserData = {};

    try {
      currentUserData =
        JSON.parse(
          localStorage.getItem(
            "userData"
          ) || "{}"
        );
    } catch {
      currentUserData = {};
    }

    localStorage.setItem(
      "userData",
      JSON.stringify({
        ...currentUserData,
        ...updatedData,
      })
    );

    localStorage.setItem(
      "company_name",
      updatedData
        .company_name ||
      ""
    );

    localStorage.setItem(
      "tenant_code",
      updatedData
        .tenant_code ||
      ""
    );

    localStorage.setItem(
      "member_prefix",
      String(
        updatedData
          .member_prefix ||
        ""
      )
    );

    localStorage.setItem(
      "licensedFor",
      String(
        updatedData
          .licensedFor ??
        ""
      )
    );

    localStorage.setItem(
      "admin_active",
      String(
        updatedData
          .admin_active ??
        0
      )
    );

    localStorage.setItem(
      "company_address",
      updatedData
        .company_address ||
      ""
    );

    localStorage.setItem(
      "company_phone",
      updatedData
        .company_phone ||
      ""
    );

    localStorage.setItem(
      "admin_email",
      updatedData
        .admin_email ||
      ""
    );

    localStorage.setItem(
      "company_logo",
      updatedData
        .company_logo ||
      ""
    );

    localStorage.setItem(
      "user_map",
      updatedData
        .user_map ||
      ""
    );

    return updatedData;
  }

  /* =======================================================
     FETCH DATA
  ======================================================= */

  const fetchPageData =
    useCallback(
      async ({
        showFullLoader = true,
        showSuccessToast = false,
      } = {}) => {
        const token =
          getToken();

        const isLoggedIn =
          localStorage.getItem(
            "isLoggedIn"
          );

        if (
          !token ||
          !isLoggedIn
        ) {
          clearAuthAndRedirect(
            navigate
          );

          return;
        }

        const localFallback =
          getLocalDashboardFallback();

        const currentRequestId =
          ++requestIdRef.current;

        try {
          if (
            showFullLoader
          ) {
            setLoading(true);
          } else {
            setRefreshing(true);
          }

          const [
            dashboardRequest,
            usersRequest,
          ] =
            await Promise.all([
              fetchNoCache(
                `${API_BASE_URL}/dashboard/admin/dashboard`,
                token,
                {
                  method:
                    "GET",
                }
              ),

              fetchNoCache(
                `${API_BASE_URL}/sentinel_users`,
                token,
                {
                  method:
                    "GET",
                }
              ),
            ]);

          if (
            currentRequestId !==
              requestIdRef.current ||
            !mountedRef.current
          ) {
            return;
          }

          const {
            response:
              dashboardRes,
            data:
              dashboardResult,
          } =
            dashboardRequest;

          const {
            response:
              usersRes,
            data:
              usersResult,
          } =
            usersRequest;

          if (
            dashboardRes.status ===
              401 ||
            dashboardRes.status ===
              403 ||
            usersRes.status ===
              401 ||
            usersRes.status ===
              403
          ) {
            clearAuthAndRedirect(
              navigate
            );

            return;
          }

          if (
            !dashboardRes.ok
          ) {
            throw new Error(
              dashboardResult
                ?.message ||
              "Failed to fetch dashboard"
            );
          }

          if (
            !usersRes.ok
          ) {
            throw new Error(
              usersResult
                ?.message ||
              "Failed to fetch sentinel users"
            );
          }

          const companyDetails =
            dashboardResult
              ?.data
              ?.companyDetails ||
            {};

          const updatedDashboardData =
            saveDashboardDataToStorage(
              companyDetails,
              localFallback
            );

          setDashboardData(
            updatedDashboardData
          );

          const rawUsers =
            usersResult
              ?.data ||
            usersResult
              ?.users ||
            [];

          const normalizedUsers =
            Array.isArray(
              rawUsers
            )
              ? rawUsers.map(
                  (
                    user,
                    index
                  ) =>
                    normalizeUser(
                      user,
                      index,
                      updatedDashboardData
                        .tenant_code
                    )
                )
              : [];

          setUsers(
            normalizedUsers
          );

          if (
            showSuccessToast
          ) {
            toast.success(
              "Latest user data fetched."
            );
          }
        } catch (error) {
          console.error(
            "[SentinelUserList] fetch error:",
            error
          );

          toast.error(
            error.message ||
            "Failed to load user list."
          );
        } finally {
          if (
            mountedRef.current &&
            currentRequestId ===
              requestIdRef.current
          ) {
            setLoading(false);
            setRefreshing(false);
          }
        }
      },
      [navigate]
    );

  /* =======================================================
     INITIAL LOAD
  ======================================================= */

  useEffect(() => {
    mountedRef.current =
      true;

    const token =
      getToken();

    const isLoggedIn =
      localStorage.getItem(
        "isLoggedIn"
      );

    if (
      !token ||
      !isLoggedIn
    ) {
      clearAuthAndRedirect(
        navigate
      );

      return undefined;
    }

    const localFallback =
      getLocalDashboardFallback();

    setDashboardData(
      localFallback
    );

    fetchPageData({
      showFullLoader:
        true,
    });

    return () => {
      mountedRef.current =
        false;
    };
  }, [
    navigate,
    fetchPageData,
  ]);

  /*
    Re-fetch whenever this route is reached again.

    The add-user page sends location.state.refresh after creating
    a user, so this list immediately retrieves the latest DB data.
  */
  useEffect(() => {
    if (
      location.state
        ?.refresh
    ) {
      fetchPageData({
        showFullLoader:
          false,
      });

      if (
        location.state
          ?.createdMemberId
      ) {
        toast.success(
          `User ${location.state.createdMemberId} created successfully.`
        );
      }

      navigate(
        location.pathname,
        {
          replace: true,
          state: null,
        }
      );
    }
  }, [
    location.state,
    location.pathname,
    fetchPageData,
    navigate,
  ]);

  /*
    Re-fetch when the browser tab becomes active again.
  */
  useEffect(() => {
    function handleVisibilityChange() {
      if (
        document.visibilityState ===
        "visible"
      ) {
        fetchPageData({
          showFullLoader:
            false,
        });
      }
    }

    function handleWindowFocus() {
      fetchPageData({
        showFullLoader:
          false,
      });
    }

    document.addEventListener(
      "visibilitychange",
      handleVisibilityChange
    );

    window.addEventListener(
      "focus",
      handleWindowFocus
    );

    return () => {
      document.removeEventListener(
        "visibilitychange",
        handleVisibilityChange
      );

      window.removeEventListener(
        "focus",
        handleWindowFocus
      );
    };
  }, [
    fetchPageData,
  ]);

  /* =======================================================
     FILTERS
  ======================================================= */

  useEffect(() => {
    setCurrentPage(1);
  }, [
    searchTerm,
    roleFilter,
    statusFilter,
  ]);

  const routePage =
    useMemo(
      () =>
        dashboardData
          .user_map ===
        "google"
          ? "/route-master"
          : "/route-master1",
      [
        dashboardData
          .user_map,
      ]
    );

  const filteredUsers =
    useMemo(() => {
      const search =
        searchTerm
          .trim()
          .toLowerCase();

      return users.filter(
        (user) => {
          const matchesSearch =
            !search ||
            String(
              user.member_id ||
              ""
            )
              .toLowerCase()
              .includes(
                search
              ) ||
            String(
              user.name ||
              ""
            )
              .toLowerCase()
              .includes(
                search
              ) ||
            String(
              user.username ||
              ""
            )
              .toLowerCase()
              .includes(
                search
              ) ||
            String(
              user.email ||
              ""
            )
              .toLowerCase()
              .includes(
                search
              ) ||
            String(
              user.tenant_code ||
              ""
            )
              .toLowerCase()
              .includes(
                search
              );

          const matchesRole =
            !roleFilter ||
            user.role ===
              roleFilter;

          const matchesStatus =
            !statusFilter ||
            user.status ===
              statusFilter;

          return (
            matchesSearch &&
            matchesRole &&
            matchesStatus
          );
        }
      );
    }, [
      users,
      searchTerm,
      roleFilter,
      statusFilter,
    ]);

  const stats =
    useMemo(() => {
      return {
        total:
          users.length,

        approved:
          users.filter(
            (user) =>
              user.status ===
              "approved"
          ).length,

        pending:
          users.filter(
            (user) =>
              user.status ===
              "pending"
          ).length,

        disapproved:
          users.filter(
            (user) =>
              user.status ===
              "disapproved"
          ).length,
      };
    }, [users]);

  const totalPages =
    Math.max(
      1,
      Math.ceil(
        filteredUsers.length /
        usersPerPage
      )
    );

  /*
    Fix current page after deleting or filtering users.
  */
  useEffect(() => {
    setCurrentPage(
      (previous) =>
        Math.min(
          previous,
          totalPages
        )
    );
  }, [totalPages]);

  const indexOfLastUser =
    currentPage *
    usersPerPage;

  const indexOfFirstUser =
    indexOfLastUser -
    usersPerPage;

  const currentUsers =
    filteredUsers.slice(
      indexOfFirstUser,
      indexOfLastUser
    );

  /* =======================================================
     STATUS UPDATE
  ======================================================= */

  async function handleApproval(
    memberId,
    action,
    checked
  ) {
    let newStatus =
      "pending";

    if (
      action ===
        "approve" &&
      checked
    ) {
      newStatus =
        "approved";
    }

    if (
      action ===
        "disapprove" &&
      checked
    ) {
      newStatus =
        "disapproved";
    }

    const token =
      getToken();

    if (!token) {
      clearAuthAndRedirect(
        navigate
      );

      return;
    }

    try {
      setStatusUpdatingMemberId(
        memberId
      );

      const {
        response,
        data,
      } =
        await fetchNoCache(
          `${API_BASE_URL}/sentinel_users/${encodeURIComponent(
            memberId
          )}/status`,
          token,
          {
            method: "PUT",

            headers: {
              "Content-Type":
                "application/json",
            },

            body:
              JSON.stringify({
                status:
                  newStatus,
              }),
          }
        );

      if (
        response.status ===
          401 ||
        response.status ===
          403
      ) {
        clearAuthAndRedirect(
          navigate
        );

        return;
      }

      if (!response.ok) {
        throw new Error(
          data?.message ||
          "Failed to update status"
        );
      }

      /*
        Update immediately for a responsive UI.
      */
      setUsers(
        (previous) =>
          previous.map(
            (user) =>
              user.member_id ===
              memberId
                ? {
                    ...user,

                    status:
                      newStatus,

                    approve_raw:
                      newStatus ===
                      "approved"
                        ? "Approved"
                        : newStatus ===
                            "disapproved"
                          ? "Disapproved"
                          : "Pending",

                    is_active:
                      newStatus ===
                      "approved"
                        ? 1
                        : 0,
                  }
                : user
          )
      );

      toast.success(
        "User status updated successfully!"
      );

      /*
        Then re-fetch from DB to guarantee the UI matches the server.
      */
      await fetchPageData({
        showFullLoader:
          false,
      });
    } catch (error) {
      console.error(
        "[SentinelUserList] Status update error:",
        error
      );

      toast.error(
        error.message ||
        "Failed to update status."
      );

      await fetchPageData({
        showFullLoader:
          false,
      });
    } finally {
      setStatusUpdatingMemberId(
        ""
      );
    }
  }

  /* =======================================================
     DELETE
  ======================================================= */

  function confirmDelete(
    memberId,
    userName
  ) {
    setDeleteModal({
      open: true,
      memberId,
      userName,
    });
  }

  function closeDeleteModal() {
    if (deleting) {
      return;
    }

    setDeleteModal({
      open: false,
      memberId: "",
      userName: "",
    });
  }

  async function handleDelete() {
    const token =
      getToken();

    if (!token) {
      clearAuthAndRedirect(
        navigate
      );

      return;
    }

    try {
      setDeleting(true);

      const {
        response,
        data,
      } =
        await fetchNoCache(
          `${API_BASE_URL}/sentinel_users/${encodeURIComponent(
            deleteModal.memberId
          )}`,
          token,
          {
            method:
              "DELETE",

            headers: {
              "Content-Type":
                "application/json",
            },
          }
        );

      if (
        response.status ===
          401 ||
        response.status ===
          403
      ) {
        clearAuthAndRedirect(
          navigate
        );

        return;
      }

      if (!response.ok) {
        throw new Error(
          data?.message ||
          "Failed to delete user"
        );
      }

      setUsers(
        (previous) =>
          previous.filter(
            (user) =>
              user.member_id !==
              deleteModal.memberId
          )
      );

      toast.success(
        "User deleted successfully!"
      );

      setDeleteModal({
        open: false,
        memberId: "",
        userName: "",
      });

      /*
        Re-fetch authoritative list from the server.
      */
      await fetchPageData({
        showFullLoader:
          false,
      });
    } catch (error) {
      console.error(
        "[SentinelUserList] Delete error:",
        error
      );

      toast.error(
        error.message ||
        "Failed to delete user."
      );

      await fetchPageData({
        showFullLoader:
          false,
      });
    } finally {
      setDeleting(false);
    }
  }

  /* =======================================================
     NAVIGATION
  ======================================================= */

  function addNewUser() {
    navigate(
      "/sentinel_add_user"
    );
  }

  function editUser(
    memberId
  ) {
    navigate(
      `/sentinel_add_user?mode=edit&member_id=${encodeURIComponent(
        memberId
      )}`
    );
  }

  function managePermissions(
    memberId
  ) {
    navigate(
      `/sentinel_guard_per?mode=edit&member_id=${encodeURIComponent(
        memberId
      )}`
    );
  }

  /* =======================================================
     LOADING
  ======================================================= */

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--page-bg)] flex flex-col items-center justify-center gap-3">
        <div className="w-9 h-9 border-3 border-[var(--accent-faint)] border-t-[var(--accent)] rounded-full animate-spin" />

        <p className="text-sm text-[var(--text-muted)] font-['Inter',system-ui,sans-serif]">
          Loading latest sentinel users...
        </p>
      </div>
    );
  }

  /* =======================================================
     UI
  ======================================================= */

  return (
    <Layout
      dashboardData={
        dashboardData
      }
      routePage={
        routePage
      }
      activePage="sentinel-user-list"
    >
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 5000,

          style: {
            background:
              "var(--card-bg)",

            color:
              "var(--text-primary)",

            border:
              "1px solid var(--card-border)",

            borderRadius:
              "12px",

            padding:
              "16px 20px",

            fontSize:
              "13px",

            fontWeight:
              "600",

            boxShadow:
              "0 8px 32px rgba(0,0,0,0.3)",
          },

          success: {
            iconTheme: {
              primary:
                "#22c55e",

              secondary:
                "white",
            },
          },

          error: {
            iconTheme: {
              primary:
                "#ef4444",

              secondary:
                "white",
            },
          },
        }}
      />

      <div className="font-['Inter',system-ui,sans-serif] text-[var(--text-primary)]">
        <div className="flex items-start justify-between gap-4 flex-wrap mb-[22px]">
          <div>
            <h1 className="text-[22px] font-extrabold text-[var(--text-primary)] tracking-tight m-0">
              Sentinel User Management
            </h1>

            <p className="text-[13px] text-[var(--text-muted)] m-0 mt-1">
              Manage all sentinel users and their permissions
            </p>
          </div>

          <button
            type="button"
            onClick={() =>
              fetchPageData({
                showFullLoader:
                  false,

                showSuccessToast:
                  true,
              })
            }
            disabled={
              refreshing ||
              deleting ||
              Boolean(
                statusUpdatingMemberId
              )
            }
            className="border border-[var(--accent-border)] bg-[var(--accent-faint)] text-[var(--accent)] rounded-xl px-4 py-2.5 text-[12px] font-extrabold disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {refreshing
              ? "Refreshing..."
              : "Refresh Latest Data"}
          </button>
        </div>

        <div className="relative overflow-hidden bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl px-7 py-6 shadow-[0_8px_32px_rgba(0,0,0,0.3)] mb-[18px]">
          <div className="absolute top-0 left-0 right-0 h-[3px] bg-[var(--accent)]" />

          <div className="flex items-center justify-between gap-3.5 flex-wrap">
            <div>
              <h2 className="text-[18px] font-extrabold text-[var(--text-primary)] m-0">
                User List
              </h2>

              <p className="text-[13px] text-[var(--text-muted)] m-0 mt-1">
                {dashboardData
                  .company_name ||
                "Company"}

                {dashboardData
                  .tenant_code
                  ? ` (${dashboardData.tenant_code})`
                  : ""}

                {dashboardData
                  .member_prefix
                  ? ` • Prefix ${dashboardData.member_prefix}`
                  : ""}
              </p>
            </div>

            <button
              type="button"
              onClick={
                addNewUser
              }
              className="border-0 rounded-xl px-4 py-2.5 text-[13px] font-extrabold cursor-pointer transition-transform duration-150 hover:opacity-95 hover:-translate-y-0.5"
              style={{
                background:
                  "var(--btn-bg)",

                color:
                  "var(--btn-text)",

                boxShadow:
                  "0 4px 18px var(--accent-glow)",
              }}
            >
              + Add New User
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-[18px]">
          <StatCard
            label="Total Users"
            value={
              stats.total
            }
            primary
          />

          <StatCard
            label="Approved"
            value={
              stats.approved
            }
          />

          <StatCard
            label="Pending"
            value={
              stats.pending
            }
          />

          <StatCard
            label="Disapproved"
            value={
              stats.disapproved
            }
          />
        </div>

        <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl px-7 py-6 shadow-[0_8px_32px_rgba(0,0,0,0.3)] mb-[18px]">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            <input
              type="text"
              placeholder="Search by name, username, email, member ID, or tenant code..."
              value={
                searchTerm
              }
              onChange={(
                event
              ) =>
                setSearchTerm(
                  event.target
                    .value
                )
              }
              className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] transition-colors duration-150 focus:border-[var(--accent)] focus:shadow-[0_0_0_3px_var(--accent-glow)] placeholder:text-[var(--text-faint)]"
            />

            <select
              value={
                roleFilter
              }
              onChange={(
                event
              ) =>
                setRoleFilter(
                  event.target
                    .value
                )
              }
              className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px]"
            >
              <option value="">
                All Roles
              </option>

              <option value="Admin">
                Admin
              </option>

              <option value="Supervisor">
                Supervisor
              </option>

              <option value="Guard">
                Guard
              </option>

              <option value="Manager">
                Manager
              </option>

              <option value="Security">
                Security
              </option>
            </select>

            <select
              value={
                statusFilter
              }
              onChange={(
                event
              ) =>
                setStatusFilter(
                  event.target
                    .value
                )
              }
              className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px]"
            >
              <option value="">
                All Status
              </option>

              <option value="approved">
                Approved
              </option>

              <option value="pending">
                Pending
              </option>

              <option value="disapproved">
                Disapproved
              </option>
            </select>
          </div>
        </div>

        <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl overflow-hidden shadow-[0_8px_32px_rgba(0,0,0,0.3)]">
          <div className="px-5 py-[18px] border-b border-[var(--divider)] flex items-center justify-between gap-3 flex-wrap">
            <p className="text-[12px] font-extrabold uppercase tracking-wider text-[var(--text-sub)] m-0">
              Sentinel Users Database
            </p>

            <span className="text-[12px] text-[var(--text-muted)]">
              {
                filteredUsers.length
              }{" "}
              entries found
            </span>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full min-w-[1200px] border-collapse">
              <thead>
                <tr
                  style={{
                    background:
                      "var(--btn-bg)",
                  }}
                >
                  {[
                    "Member ID",
                    "Tenant",
                    "User",
                    "Username",
                    "Password",
                    "Role",
                    "Status",
                    "Approval",
                    "Actions",
                  ].map(
                    (heading) => (
                      <th
                        key={
                          heading
                        }
                        className="px-5 py-4 text-left text-[12px] font-extrabold uppercase tracking-wider text-white"
                      >
                        {heading}
                      </th>
                    )
                  )}
                </tr>
              </thead>

              <tbody>
                {currentUsers.length ===
                0 ? (
                  <tr>
                    <td
                      colSpan={9}
                      className="text-center py-8 text-[var(--text-muted)]"
                    >
                      No users found in the database.
                    </td>
                  </tr>
                ) : (
                  currentUsers.map(
                    (user) => (
                      <tr
                        key={
                          user.member_id ||
                          user.id
                        }
                        className="border-b border-[var(--divider)] hover:bg-[var(--accent-faint)] transition-colors"
                      >
                        <td className="px-5 py-4 align-middle text-[13px]">
                          <span className="text-[var(--accent)] font-extrabold">
                            {user.member_id ||
                              "—"}
                          </span>
                        </td>

                        <td className="px-5 py-4 align-middle">
                          <span className="inline-flex items-center rounded-full px-3 py-1.5 text-[11.5px] font-extrabold bg-[var(--accent-faint)] border border-[var(--accent-border)] text-[var(--accent)]">
                            {user.tenant_code ||
                              dashboardData
                                .tenant_code ||
                              "—"}
                          </span>
                        </td>

                        <td className="px-5 py-4 align-middle">
                          <div className="flex items-center gap-3">
                            <div
                              className="w-10 h-10 shrink-0 rounded-xl flex items-center justify-center text-[14px] font-extrabold text-white shadow-[0_3px_12px_var(--accent-glow)]"
                              style={{
                                background:
                                  "var(--btn-bg)",
                              }}
                            >
                              {String(
                                user.name ||
                                "U"
                              )
                                .charAt(
                                  0
                                )
                                .toUpperCase()}
                            </div>

                            <div>
                              <div className="text-[13px] font-extrabold text-[var(--text-primary)]">
                                {user.name ||
                                  "—"}
                              </div>

                              <div className="text-[12px] text-[var(--text-muted)]">
                                {user.email ||
                                  "—"}
                              </div>
                            </div>
                          </div>
                        </td>

                        <td className="px-5 py-4 align-middle text-[13px] text-[var(--text-primary)]">
                          {user.username ||
                            "—"}
                        </td>

                        <td className="px-5 py-4 align-middle font-mono text-[13px] tracking-[2px] text-[var(--text-primary)]">
                          {
                            user.password_display
                          }
                        </td>

                        <td className="px-5 py-4 align-middle">
                          <RoleBadge
                            role={
                              user.role
                            }
                          />
                        </td>

                        <td className="px-5 py-4 align-middle">
                          <StatusBadge
                            status={
                              user.status
                            }
                          />
                        </td>

                        <td className="px-5 py-4 align-middle">
                          <div className="flex flex-col gap-1.5">
                            <label
                              className={`flex items-center gap-2 text-[12px] font-bold text-[#22c55e] ${
                                statusUpdatingMemberId ===
                                user.member_id
                                  ? "opacity-50 cursor-not-allowed"
                                  : "cursor-pointer"
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={
                                  user.status ===
                                  "approved"
                                }
                                disabled={
                                  statusUpdatingMemberId ===
                                  user.member_id
                                }
                                onChange={(
                                  event
                                ) =>
                                  handleApproval(
                                    user.member_id,
                                    "approve",
                                    event
                                      .target
                                      .checked
                                  )
                                }
                                className="w-4 h-4 accent-[var(--accent)] cursor-pointer disabled:cursor-not-allowed"
                              />

                              Approve
                            </label>

                            <label
                              className={`flex items-center gap-2 text-[12px] font-bold text-[var(--red)] ${
                                statusUpdatingMemberId ===
                                user.member_id
                                  ? "opacity-50 cursor-not-allowed"
                                  : "cursor-pointer"
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={
                                  user.status ===
                                  "disapproved"
                                }
                                disabled={
                                  statusUpdatingMemberId ===
                                  user.member_id
                                }
                                onChange={(
                                  event
                                ) =>
                                  handleApproval(
                                    user.member_id,
                                    "disapprove",
                                    event
                                      .target
                                      .checked
                                  )
                                }
                                className="w-4 h-4 accent-[var(--accent)] cursor-pointer disabled:cursor-not-allowed"
                              />

                              Disapprove
                            </label>
                          </div>
                        </td>

                        <td className="px-5 py-4 align-middle">
                          <div className="flex flex-wrap gap-2">
                            <ActionButton
                              label="Edit"
                              onClick={() =>
                                editUser(
                                  user.member_id
                                )
                              }
                            />

                            <ActionButton
                              label="Permissions"
                              onClick={() =>
                                managePermissions(
                                  user.member_id
                                )
                              }
                            />

                            <ActionButton
                              label="Delete"
                              danger
                              onClick={() =>
                                confirmDelete(
                                  user.member_id,
                                  user.name
                                )
                              }
                            />
                          </div>
                        </td>
                      </tr>
                    )
                  )
                )}
              </tbody>
            </table>
          </div>

          <div className="px-5 py-[15px] border-t border-[var(--divider)] flex items-center justify-between gap-3 flex-wrap">
            <div className="text-[12px] text-[var(--text-muted)]">
              Showing{" "}
              {currentUsers.length ===
              0
                ? 0
                : indexOfFirstUser +
                  1}{" "}
              to{" "}
              {Math.min(
                indexOfLastUser,
                filteredUsers.length
              )}{" "}
              of{" "}
              {
                filteredUsers.length
              }{" "}
              entries
            </div>

            <div className="flex gap-1.5 flex-wrap">
              <button
                type="button"
                disabled={
                  currentPage <= 1
                }
                onClick={() =>
                  setCurrentPage(
                    (
                      previous
                    ) =>
                      Math.max(
                        1,
                        previous -
                          1
                      )
                  )
                }
                className="border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] rounded-xl px-3 py-2 text-[12px] font-extrabold cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>

              {Array.from(
                {
                  length:
                    totalPages,
                },
                (
                  _,
                  index
                ) =>
                  index + 1
              ).map(
                (
                  pageNumber
                ) => (
                  <button
                    type="button"
                    key={
                      pageNumber
                    }
                    onClick={() =>
                      setCurrentPage(
                        pageNumber
                      )
                    }
                    className={`border rounded-xl px-3 py-2 text-[12px] font-extrabold cursor-pointer ${
                      currentPage ===
                      pageNumber
                        ? "bg-[var(--btn-bg)] text-[var(--btn-text)] border-[var(--accent-border)]"
                        : "border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)]"
                    }`}
                  >
                    {pageNumber}
                  </button>
                )
              )}

              <button
                type="button"
                disabled={
                  currentPage >=
                  totalPages
                }
                onClick={() =>
                  setCurrentPage(
                    (
                      previous
                    ) =>
                      Math.min(
                        totalPages,
                        previous +
                          1
                      )
                  )
                }
                className="border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] rounded-xl px-3 py-2 text-[12px] font-extrabold cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        </div>

        {deleteModal.open && (
          <div
            className="fixed inset-0 z-[9999] bg-black/55 flex items-center justify-center p-[18px]"
            onClick={
              closeDeleteModal
            }
          >
            <div
              className="w-full max-w-[460px] bg-[var(--card-bg)] border border-[var(--card-border)] rounded-[18px] p-6 shadow-[0_18px_50px_rgba(0,0,0,0.45)]"
              onClick={(
                event
              ) =>
                event.stopPropagation()
              }
            >
              <h3 className="text-[20px] font-extrabold text-[var(--text-primary)] m-0 mb-2.5">
                Confirm Delete
              </h3>

              <p className="text-[13px] text-[var(--text-muted)] m-0 mb-3 leading-relaxed">
                Are you sure you want to delete user{" "}
                <strong>
                  {deleteModal
                    .userName ||
                  "this user"}
                </strong>
                ?
              </p>

              <p className="text-[12px] text-[var(--red)] m-0 mb-[18px] font-bold">
                This action cannot be undone.
              </p>

              <div className="flex justify-end gap-2.5">
                <button
                  type="button"
                  onClick={
                    closeDeleteModal
                  }
                  disabled={
                    deleting
                  }
                  className="rounded-xl px-3.5 py-2.5 text-[13px] font-extrabold cursor-pointer bg-[var(--surface)] border border-[var(--surface-border)] text-[var(--text-primary)] disabled:opacity-60"
                >
                  Cancel
                </button>

                <button
                  type="button"
                  onClick={
                    handleDelete
                  }
                  disabled={
                    deleting
                  }
                  className="rounded-xl px-3.5 py-2.5 text-[13px] font-extrabold cursor-pointer bg-[var(--red)] border border-[var(--red)] text-white disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {deleting
                    ? "Deleting..."
                    : "Delete User"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

/* =========================================================
   SMALL COMPONENTS
========================================================= */

function StatCard({
  label,
  value,
  primary = false,
}) {
  return (
    <div
      className={
        primary
          ? "relative overflow-hidden rounded-xl p-4 bg-[var(--btn-bg)] border border-[var(--surface-border)] shadow-[0_4px_18px_var(--accent-glow)]"
          : "relative overflow-hidden rounded-xl p-4 bg-[var(--surface)] border border-[var(--surface-border)]"
      }
    >
      <p
        className={
          primary
            ? "text-[10px] font-extrabold uppercase tracking-wider text-[var(--btn-text)] opacity-75 m-0 mb-1.5"
            : "text-[10px] font-extrabold uppercase tracking-wider text-[var(--text-faint)] m-0 mb-1.5"
        }
      >
        {label}
      </p>

      <p
        className={
          primary
            ? "text-[30px] font-extrabold leading-none m-0 text-[var(--btn-text)]"
            : "text-[30px] font-extrabold leading-none m-0 text-[var(--accent)]"
        }
      >
        {value}
      </p>
    </div>
  );
}

function ActionButton({
  label,
  onClick,
  danger = false,
}) {
  return (
    <button
      type="button"
      onClick={
        onClick
      }
      className={
        danger
          ? "border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] rounded-xl px-2.5 py-2 text-[12px] font-extrabold cursor-pointer transition-colors duration-150 hover:bg-[var(--red-faint)] hover:border-[var(--red-border)] hover:text-[var(--red)]"
          : "border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] rounded-xl px-2.5 py-2 text-[12px] font-extrabold cursor-pointer transition-colors duration-150 hover:bg-[var(--accent-faint)] hover:border-[var(--accent-border)] hover:text-[var(--accent)]"
      }
    >
      {label}
    </button>
  );
}

function RoleBadge({
  role,
}) {
  const lower =
    String(
      role || ""
    ).toLowerCase();

  let background =
    "bg-[rgba(34,197,94,0.12)]";

  let border =
    "border-[rgba(34,197,94,0.28)]";

  let text =
    "text-[#22c55e]";

  if (
    lower === "admin"
  ) {
    background =
      "bg-[var(--accent-faint)]";

    border =
      "border-[var(--accent-border)]";

    text =
      "text-[var(--accent)]";
  } else if (
    lower ===
    "supervisor"
  ) {
    background =
      "bg-[rgba(168,85,247,0.12)]";

    border =
      "border-[rgba(168,85,247,0.28)]";

    text =
      "text-[#a855f7]";
  } else if (
    lower ===
    "security"
  ) {
    background =
      "bg-[rgba(132,204,22,0.12)]";

    border =
      "border-[rgba(132,204,22,0.28)]";

    text =
      "text-[#84cc16]";
  }

  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11.5px] font-extrabold border ${background} ${border} ${text}`}
    >
      {role || "—"}
    </span>
  );
}

function StatusBadge({
  status,
}) {
  const value =
    String(
      status ||
      "pending"
    ).toLowerCase();

  let background =
    "bg-[rgba(245,158,11,0.12)]";

  let border =
    "border-[rgba(245,158,11,0.28)]";

  let text =
    "text-[#f59e0b]";

  if (
    value ===
    "approved"
  ) {
    background =
      "bg-[rgba(34,197,94,0.12)]";

    border =
      "border-[rgba(34,197,94,0.28)]";

    text =
      "text-[#22c55e]";
  } else if (
    value ===
    "disapproved"
  ) {
    background =
      "bg-[var(--red-faint)]";

    border =
      "border-[var(--red-border)]";

    text =
      "text-[var(--red)]";
  }

  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11.5px] font-extrabold border ${background} ${border} ${text}`}
    >
      ●{" "}
      {value
        .charAt(0)
        .toUpperCase() +
        value.slice(1)}
    </span>
  );
}